import { useState, useEffect, useMemo, useRef } from "react";
import * as XLSX from "xlsx";
import PropTypes from "prop-types";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

// Modal component
import MDModal from "components/MDModal";
import DetallesEmpresa from "./components/DetallesEmpresa"; // Componente a crear
import MDDeleteModal from "components/MDDeleteModal";

// Custom Pagination component
import Pagination from "components/common/pagination/Pagination";

// Services
import empresaService from "services/empresaService"; // Servicio a crear/adaptar

// Context
import { useMaterialUIController } from "context";

const ITEMS_PER_PAGE = 5;

// Función para convertir números de serie de Excel a fechas (por si se necesita en futuro)
const excelDateToJSDate = (excelDate) => {
  if (typeof excelDate === 'string' && excelDate.includes('-')) {
    return excelDate;
  }
  if (typeof excelDate === 'number') {
    const jsDate = new Date((excelDate - 25569) * 86400 * 1000);
    return jsDate.toISOString().split('T')[0];
  }
  if (excelDate instanceof Date) {
    return excelDate.toISOString().split('T')[0];
  }
  return excelDate;
};

// Componente para renderizar el texto de las celdas
const TextCell = ({ value }) => (
  <MDTypography variant="caption" fontWeight="medium">
    {value || ""}
  </MDTypography>
);
TextCell.propTypes = {
  value: PropTypes.any,
};

// Componente para las acciones de cada fila
const ActionsCell = ({ empresaId, onEdit, onDelete, onViewDetails }) => (
  <MDBox display="flex" justifyContent="center" alignItems="center">
    <MDButton
      iconOnly
      color="success"
      size="small"
      sx={{ mr: 1 }}
      onClick={() => onViewDetails(empresaId)}
    >
      <Icon>visibility</Icon>
    </MDButton>
    <MDButton
      iconOnly
      color="info"
      size="small"
      sx={{ mr: 1 }}
      onClick={() => onEdit(empresaId)}
    >
      <Icon>edit</Icon>
    </MDButton>
    <MDButton
      iconOnly
      color="error"
      size="small"
      onClick={() => onDelete(empresaId)}
    >
      <Icon>delete</Icon>
    </MDButton>
  </MDBox>
);
ActionsCell.propTypes = {
  empresaId: PropTypes.string.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onViewDetails: PropTypes.func.isRequired,
};

function CrearEmpresa() {
  const [controller] = useMaterialUIController();
  const { searchTerm } = controller;

  // Ref para el formulario individual
  const formRef = useRef(null);

  const [tableData, setTableData] = useState({ columns: [], rows: [] });
  const [loading, setLoading] = useState(false);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    color: "success",
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [allEmpresas, setAllEmpresas] = useState([]);

  // Estado para el modal de detalles
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedEmpresa, setSelectedEmpresa] = useState(null);

  // Estados para el modal de eliminación
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [empresaToDelete, setEmpresaToDelete] = useState(null);

  // Estado para el formulario individual - Campos de Empresa
  const [formData, setFormData] = useState({
    nombreEmpresa: "",
    nit: "",
    correo: "",
    telefono: "",
    direccion: "",
    ciudad: "",
    personaContacto: "",
    cargo: "",
    comentarios: "",
  });
  const [editingEmpresaId, setEditingEmpresaId] = useState(null);

  // Configurar columnas de la tabla
  const tableColumns = [
    { Header: "Nombre de la empresa", accessor: "nombre", width: "15%" },
    { Header: "NIT", accessor: "nit", width: "10%" },
    { Header: "Correo", accessor: "correo", width: "15%" },
    { Header: "Teléfono", accessor: "telefono", width: "10%" },
    { Header: "Dirección", accessor: "direccion", width: "12%" },
    { Header: "Ciudad", accessor: "ciudad", width: "10%" },
    { Header: "Persona de contacto", accessor: "persona_contacto", width: "12%" },
    { Header: "Cargo", accessor: "cargo", width: "10%" },
    { Header: "Comentarios", accessor: "comentarios", width: "10%" },
    { Header: "Acciones", accessor: "acciones", width: "8%" },
  ];

  useEffect(() => {
    loadEmpresas();
  }, []);

  const loadEmpresas = async () => {
    try {
      setLoading(true);
      const empresas = await empresaService.getAllEmpresas();
      // Ordenar empresas por updated_at descendente
      const sortedEmpresas = empresas.sort((a, b) => {
        const dateA = new Date(a.updated_at);
        const dateB = new Date(b.updated_at);
        return dateB.getTime() - dateA.getTime();
      });
      setAllEmpresas(sortedEmpresas);
    } catch (error) {
      showNotification(error.message || "Error al cargar empresas", "error");
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message, color = "success") => {
    setNotification({
      open: true,
      message,
      color,
    });
  };

  const closeNotification = () => {
    setNotification({
      ...notification,
      open: false,
    });
  };

  const handleInputChange = (field, value) => {
    // Validación especial para NIT (solo números, máximo 10 dígitos)
    if (field === "nit") {
      const numericValue = value.replace(/\D/g, "");
      if (numericValue.length <= 10) {
        setFormData(prev => ({ ...prev, [field]: numericValue }));
      }
      return;
    }

    // Validación para teléfono (solo números)
    if (field === "telefono") {
      const numericValue = value.replace(/\D/g, "");
      if (numericValue.length <= 15) {
        setFormData(prev => ({ ...prev, [field]: numericValue }));
      }
      return;
    }

    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleClearForm = () => {
    setFormData({
      nombreEmpresa: "",
      nit: "",
      correo: "",
      telefono: "",
      direccion: "",
      ciudad: "",
      personaContacto: "",
      cargo: "",
      comentarios: "",
    });
    setEditingEmpresaId(null);
  };

  const handleSubmitIndividual = async () => {
    try {
      setFormSubmitting(true);

      // Validar campos requeridos
      const requiredFields = [
        { field: "nombreEmpresa", label: "Nombre de la empresa" },
        { field: "nit", label: "NIT" },
        { field: "correo", label: "Correo" },
      ];

      const missingFields = requiredFields.filter(({ field }) => !formData[field].trim());

      if (missingFields.length > 0) {
        const missingLabels = missingFields.map(({ label }) => label).join(", ");
        showNotification(`Por favor completa los campos requeridos: ${missingLabels}`, "error");
        return;
      }

      // Validar email
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.correo)) {
        showNotification("Por favor ingresa un correo electrónico válido", "error");
        return;
      }

      let resultEmpresa;
      if (editingEmpresaId) {
        resultEmpresa = await empresaService.updateEmpresa(editingEmpresaId, formData);
        showNotification("Empresa editada exitosamente", "success");
      } else {
        resultEmpresa = await empresaService.createEmpresa(formData);
        showNotification("Empresa creada exitosamente", "success");
      }

      // Re-fetch todas las empresas
      const latestEmpresas = await empresaService.getAllEmpresas();

      let finalSortedEmpresas = [];
      if (resultEmpresa) {
        const otherEmpresas = latestEmpresas.filter(e => e.id_empresa !== resultEmpresa.id_empresa);
        const sortedOtherEmpresas = otherEmpresas.sort((a, b) => {
          const dateA = new Date(a.updated_at);
          const dateB = new Date(b.updated_at);
          return dateB.getTime() - dateA.getTime();
        });
        finalSortedEmpresas = [resultEmpresa, ...sortedOtherEmpresas];
      } else {
        finalSortedEmpresas = latestEmpresas.sort((a, b) => {
          const dateA = new Date(a.updated_at);
          const dateB = new Date(b.updated_at);
          return dateB.getTime() - dateA.getTime();
        });
      }

      setAllEmpresas(finalSortedEmpresas);
      setCurrentPage(1);

      // Limpiar formulario
      handleClearForm();

    } catch (error) {
      const action = editingEmpresaId ? 'actualizar' : 'crear';
      showNotification(error.message || `Error al ${action} empresa`, "error");
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleEdit = async (empresaId) => {
    try {
      setLoading(true);
      const empresa = await empresaService.getEmpresaById(empresaId);
      if (empresa) {
        const formattedData = empresaService.transformAPIDataToForm(empresa);
        setFormData(formattedData);
        setEditingEmpresaId(empresaId);
        showNotification("Empresa cargada para edición", "info");
        if (formRef.current) {
          formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    } catch (error) {
      showNotification(error.message || "Error al cargar empresa", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (empresaId) => {
    const empresa = allEmpresas.find(e => e.id_empresa === empresaId);
    if (empresa) {
      setEmpresaToDelete(empresa);
      setDeleteModalOpen(true);
    }
  };

  const handleConfirmDelete = async () => {
    if (!empresaToDelete) return;

    try {
      setLoading(true);
      await empresaService.deleteEmpresa(empresaToDelete.id_empresa);
      await loadEmpresas();
      showNotification("Empresa eliminada exitosamente", "success");
    } catch (error) {
      showNotification(error.message || "Error al eliminar empresa", "error");
    } finally {
      setLoading(false);
      setDeleteModalOpen(false);
      setEmpresaToDelete(null);
    }
  };

  const handleViewDetails = (empresaId) => {
    const empresa = allEmpresas.find(e => e.id_empresa === empresaId);
    if (empresa) {
      setSelectedEmpresa(empresa);
      setModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedEmpresa(null);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Filtrar empresas basándose en el searchTerm
  const filteredEmpresas = useMemo(() => {
    if (!searchTerm) {
      return allEmpresas;
    }
    const lowercasedSearchTerm = searchTerm.toLowerCase();
    return allEmpresas.filter((empresa) => {
      const fieldContains = (field) => {
        return field && typeof field === 'string' && field.toLowerCase().includes(lowercasedSearchTerm);
      };

      return fieldContains(empresa.nombre_empresa) ||
             fieldContains(empresa.nit?.toString()) ||
             fieldContains(empresa.correo) ||
             fieldContains(empresa.telefono) ||
             fieldContains(empresa.direccion) ||
             fieldContains(empresa.ciudad) ||
             fieldContains(empresa.persona_contacto) ||
             fieldContains(empresa.cargo) ||
             fieldContains(empresa.comentarios);
    });
  }, [allEmpresas, searchTerm]);

  // Reset page to 1 when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Paginación y formateo de datos para la tabla
  const { paginatedEmpresas, totalItems, formattedRows } = useMemo(() => {
    const total = filteredEmpresas.length;
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    const end = start + ITEMS_PER_PAGE;
    const paginated = filteredEmpresas.slice(start, end);

    const rows = paginated.map((empresa) => ({
      nombre: <TextCell value={empresa.nombre_empresa} />,
      nit: <TextCell value={empresa.nit} />,
      correo: <TextCell value={empresa.correo} />,
      telefono: <TextCell value={empresa.telefono} />,
      direccion: <TextCell value={empresa.direccion} />,
      ciudad: <TextCell value={empresa.ciudad} />,
      persona_contacto: <TextCell value={empresa.persona_contacto} />,
      cargo: <TextCell value={empresa.cargo} />,
      comentarios: <TextCell value={empresa.comentarios} />,
      acciones: <ActionsCell 
        empresaId={empresa.id_empresa} 
        onEdit={handleEdit} 
        onDelete={handleDelete} 
        onViewDetails={handleViewDetails} 
      />,
    }));

    return { paginatedEmpresas: paginated, totalItems: total, formattedRows: rows };
  }, [filteredEmpresas, currentPage]);

  useEffect(() => {
    setTableData({
      columns: tableColumns,
      rows: formattedRows
    });
  }, [formattedRows]);

  const handleDownloadTemplate = () => {
    const headers = [
      "Nombre de la empresa",
      "NIT",
      "Correo",
      "Telefono",
      "Direccion",
      "Ciudad",
      "Persona de contacto principal",
      "Cargo",
      "Comentarios",
    ];

    const ws = XLSX.utils.aoa_to_sheet([headers]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Plantilla");
    XLSX.writeFile(wb, "Plantilla_Empresas.xlsx");

    showNotification("Plantilla descargada correctamente", "success");
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        setLoading(true);
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
          raw: false,
          dateNF: 'yyyy-mm-dd'
        });

        if (jsonData.length === 0) {
          showNotification("El archivo está vacío o no tiene datos válidos", "warning");
          return;
        }

        // Usar el servicio para carga masiva
        const result = await empresaService.createBulkEmpresas(jsonData);

        // Recargar la tabla
        await loadEmpresas();

        // Limpiar el input de archivo
        event.target.value = '';

        // Mostrar resultado
        if (result.errors.length > 0) {
          showNotification(
            `Carga completada: ${result.success} exitosos, ${result.errors.length} errores`,
            "warning"
          );
        } else {
          showNotification(`Archivo cargado correctamente: ${result.success} empresas agregadas`, "success");
        }

      } catch (error) {
        console.error("Error al procesar el archivo:", error);
        showNotification(error.message || "Error al procesar el archivo", "error");
      } finally {
        setLoading(false);
      }
    };

    reader.readAsArrayBuffer(file);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          {/* Formulario de Crear Empresa */}
          <Grid item xs={12}>
            <Card ref={formRef}>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Crear Empresa
                </MDTypography>
              </MDBox>
              <MDBox p={3}>
                <Grid container spacing={3}>
                  {/* Carga Masiva y Generación de Plantilla */}
                  <Grid item xs={12} container spacing={3} alignItems="flex-end">
                    <Grid item xs={12} md={7}>
                      <MDTypography variant="h6" mb={1}>CARGAR PLANTILLA MASIVA DE EMPRESAS</MDTypography>
                      <MDInput
                        type="file"
                        fullWidth
                        accept=".xlsx,.xls"
                        onChange={handleFileUpload}
                        disabled={loading}
                      />
                    </Grid>
                    <Grid item xs={12} md={5}>
                      <MDTypography variant="h6" mb={1}>DESCARGA TU PLANTILLA</MDTypography>
                      <MDButton
                        variant="gradient"
                        color="info"
                        fullWidth
                        onClick={handleDownloadTemplate}
                        disabled={loading}
                      >
                        <Icon>download</Icon>&nbsp; Descargar Plantilla
                      </MDButton>
                    </Grid>
                  </Grid>

                  {/* Formulario Individual */}
                  <Grid item xs={12}>
                    <MDTypography variant="h6">CREAR EMPRESA INDIVIDUAL</MDTypography>
                  </Grid>

                  {/* Campos del formulario - 3 columnas por fila según diseño */}
                  <Grid item xs={12} md={4}>
                    <MDInput
                      label="Nombre de la empresa *"
                      fullWidth
                      value={formData.nombreEmpresa}
                      onChange={(e) => handleInputChange("nombreEmpresa", e.target.value)}
                      disabled={formSubmitting}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <MDInput
                      label="NIT *"
                      fullWidth
                      value={formData.nit}
                      onChange={(e) => handleInputChange("nit", e.target.value)}
                      inputProps={{ maxLength: 10 }}
                      helperText="Máximo 10 dígitos"
                      disabled={formSubmitting}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <MDInput
                      label="Correo *"
                      type="email"
                      fullWidth
                      value={formData.correo}
                      onChange={(e) => handleInputChange("correo", e.target.value)}
                      disabled={formSubmitting}
                    />
                  </Grid>

                  {/* Segunda fila */}
                  <Grid item xs={12} md={4}>
                    <MDInput
                      label="Teléfono"
                      fullWidth
                      value={formData.telefono}
                      onChange={(e) => handleInputChange("telefono", e.target.value)}
                      disabled={formSubmitting}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <MDInput
                      label="Dirección"
                      fullWidth
                      value={formData.direccion}
                      onChange={(e) => handleInputChange("direccion", e.target.value)}
                      disabled={formSubmitting}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <MDInput
                      label="Ciudad"
                      fullWidth
                      value={formData.ciudad}
                      onChange={(e) => handleInputChange("ciudad", e.target.value)}
                      disabled={formSubmitting}
                    />
                  </Grid>

                  {/* Tercera fila */}
                  <Grid item xs={12} md={4}>
                    <MDInput
                      label="Persona de contacto principal"
                      fullWidth
                      value={formData.personaContacto}
                      onChange={(e) => handleInputChange("personaContacto", e.target.value)}
                      disabled={formSubmitting}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <MDInput
                      label="Cargo"
                      fullWidth
                      value={formData.cargo}
                      onChange={(e) => handleInputChange("cargo", e.target.value)}
                      disabled={formSubmitting}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <MDInput
                      label="Comentarios"
                      fullWidth
                      multiline
                      rows={1}
                      value={formData.comentarios}
                      onChange={(e) => handleInputChange("comentarios", e.target.value)}
                      disabled={formSubmitting}
                    />
                  </Grid>

                  {/* Botones de acción */}
                  <Grid item xs={12}>
                    <MDBox display="flex" justifyContent="flex-end" mt={2}>
                      <MDButton
                        variant="outlined"
                        color="secondary"
                        onClick={handleClearForm}
                        disabled={formSubmitting}
                        sx={{ mr: 1 }}
                      >
                        Cancelar
                      </MDButton>
                      <MDButton
                        variant="gradient"
                        color="success"
                        onClick={handleSubmitIndividual}
                        disabled={formSubmitting}
                      >
                        {formSubmitting
                          ? "Procesando..."
                          : (editingEmpresaId ? "Guardar Cambios" : "Guardar")
                        }
                      </MDButton>
                    </MDBox>
                  </Grid>
                </Grid>
              </MDBox>
            </Card>
          </Grid>

          {/* Tabla de Empresas */}
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Lista de Empresas ({totalItems} total{filteredEmpresas.length !== allEmpresas.length ? `, ${filteredEmpresas.length} filtradas` : ""})
                </MDTypography>
              </MDBox>
              <MDBox pt={3}>
                <DataTable
                  table={tableData}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                  canSearch={false}
                />
              </MDBox>
              <MDBox pt={3}>
                <Pagination
                  itemsPerPage={ITEMS_PER_PAGE}
                  totalItems={totalItems}
                  currentPage={currentPage}
                  onPageChange={handlePageChange}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      {/* Modal para mostrar detalles */}
      <MDModal
        open={modalOpen}
        onClose={handleCloseModal}
        title="Detalles de la Empresa"
        size="lg"
      >
        {selectedEmpresa && <DetallesEmpresa empresa={selectedEmpresa} />}
      </MDModal>

      {/* Modal de confirmación de eliminación */}
      <MDDeleteModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        item={empresaToDelete}
        itemType="empresa"
        onDelete={handleConfirmDelete}
      />

      {/* Notificaciones */}
      <MDSnackbar
        color={notification.color}
        icon="notifications"
        title="Notificación"
        content={notification.message || ""}
        dateTime={new Date().toLocaleTimeString()}
        open={notification.open}
        onClose={closeNotification}
        close={closeNotification}
        bgWhite
      />
    </DashboardLayout>
  );
}

export default CrearEmpresa;